import CryptoJS from 'crypto-js';
import { inflate } from 'pako';

import AppSettings from '../../constants';

/**
 * Decodes URL-safe Base64 string without padding
 */
function decodeUrlSafeBase64(encoded: string): string {
    // Add padding if needed
    let padded = encoded;
    while (padded.length % 4) {
        padded += '=';
    }

    // Replace URL-safe characters with standard Base64 characters
    const standardBase64 = padded.replace(/-/g, '+').replace(/_/g, '/');

    return CryptoJS.enc.Base64.parse(standardBase64).toString(CryptoJS.enc.Latin1);
}

/**
 * Normalizes encryption key to exactly 16 bytes for AES-128
 * If shorter: pad with null bytes
 * If longer: truncate to 16 bytes
 */
function normalizeKey(key: string): CryptoJS.lib.WordArray {
    const keyBytes = CryptoJS.enc.Utf8.parse(key);

    if (keyBytes.sigBytes === 16) {
        return keyBytes;
    }

    // Create a 16-byte array
    const normalizedBytes = new Uint8Array(16);

    if (keyBytes.sigBytes < 16) {
        // Pad with null bytes
        const sourceBytes = new Uint8Array(
            keyBytes.words
                .flatMap((word) => [(word >>> 24) & 0xff, (word >>> 16) & 0xff, (word >>> 8) & 0xff, word & 0xff])
                .slice(0, keyBytes.sigBytes)
        );

        normalizedBytes.set(sourceBytes);
        // Rest remains as null bytes (0)
    } else {
        // Truncate to 16 bytes
        const sourceBytes = new Uint8Array(
            keyBytes.words
                .flatMap((word) => [(word >>> 24) & 0xff, (word >>> 16) & 0xff, (word >>> 8) & 0xff, word & 0xff])
                .slice(0, 16)
        );

        normalizedBytes.set(sourceBytes);
    }

    // Convert back to WordArray
    const words: number[] = [];
    for (let i = 0; i < 16; i += 4) {
        words.push(
            (normalizedBytes[i] << 24) |
                (normalizedBytes[i + 1] << 16) |
                (normalizedBytes[i + 2] << 8) |
                normalizedBytes[i + 3]
        );
    }

    return CryptoJS.lib.WordArray.create(words, 16);
}

/**
 * Decompresses data using inflate (matching Java's Deflater.BEST_COMPRESSION)
 */
function decompressData(compressedBytes: Uint8Array): string {
    try {
        const decompressed = inflate(compressedBytes);
        return new TextDecoder('utf-8').decode(decompressed);
    } catch {
        throw new Error('Failed to decompress data');
    }
}

/**
 * Decrypts AES-128-CBC encrypted and compressed token
 * Token structure: [16-byte IV][encrypted compressed JWT data] encoded as URL-safe Base64
 */
function decryptApprovalToken(encryptedToken: string): string {
    if (!encryptedToken) {
        throw new Error('Encrypted token is empty');
    }

    if (!AppSettings.approvalTokenEncryptionKey) {
        throw new Error('Encryption key not configured');
    }

    // Decode the URL-safe Base64 encoded token
    const decodedBytes = decodeUrlSafeBase64(encryptedToken);

    if (decodedBytes.length < 16) {
        throw new Error('Invalid token format: too short to contain IV');
    }

    // Extract IV (first 16 bytes) and encrypted data (rest)
    const iv = CryptoJS.enc.Latin1.parse(decodedBytes.substring(0, 16));
    const encryptedData = CryptoJS.enc.Latin1.parse(decodedBytes.substring(16));

    // Normalize the encryption key
    const key = normalizeKey(AppSettings.approvalTokenEncryptionKey);

    // Decrypt using AES-128-CBC
    const decrypted = CryptoJS.AES.decrypt(
        CryptoJS.lib.CipherParams.create({
            ciphertext: encryptedData,
            iv: iv,
            salt: CryptoJS.lib.WordArray.create([]), // No salt for CBC mode
        }),
        key,
        {
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
            iv: iv,
        }
    );

    // Convert decrypted data to bytes for decompression
    const decryptedHex = decrypted.toString(CryptoJS.enc.Hex);
    const decryptedBytes = new Uint8Array(decryptedHex.length / 2);
    for (let i = 0; i < decryptedHex.length; i += 2) {
        decryptedBytes[i / 2] = parseInt(decryptedHex.substr(i, 2), 16);
    }

    // Decompress the decrypted data
    const decompressedToken = decompressData(decryptedBytes);

    if (!decompressedToken) {
        throw new Error('Decryption failed: empty result after decompression');
    }

    return decompressedToken;
}

/**
 * Safely attempts to decrypt an approval token with fallback
 * Returns the decrypted token on success, null on failure
 */
export function safeDecryptApprovalToken(encryptedToken: string): string | null {
    try {
        return decryptApprovalToken(encryptedToken);
    } catch {
        // Silently handle decryption failures for graceful fallback
        return null;
    }
}
